import {
  ServerArtifactSchema,
  canonicalSha256,
  sha256Hex,
  type ToolDesign,
  type ServerArtifact,
  type ServerFile,
  type ServerKind
} from "@keystone/core";
import {
  renderAudit,
  renderAuth,
  renderHttp,
  renderMiddleware,
  renderPackageJson,
  renderProxy,
  renderReadme,
  renderServer,
  renderStdio,
  renderTsConfig
} from "./templates.js";

export interface GenerateOptions {
  /**
   * Deterministic timestamp. CI passes a content-addressed value (e.g., the spec digest)
   * so the artifact's `generatedAt` doesn't churn across runs.
   */
  generatedAt?: string;
}

export function generateDesigned(design: ToolDesign, opt: GenerateOptions = {}): ServerArtifact {
  return generate("designed", design, opt);
}

export function generateNaiveBaseline(design: ToolDesign, opt: GenerateOptions = {}): ServerArtifact {
  return generate("naive-baseline", design, opt);
}

function generate(kind: ServerKind, design: ToolDesign, opt: GenerateOptions): ServerArtifact {
  const designedKind = kind === "designed";
  const files: ServerFile[] = [];

  function push(path: string, content: string): void {
    files.push({ path, content, digest: sha256Hex(content) });
  }

  push("package.json", renderPackageJson(design));
  push("tsconfig.json", renderTsConfig());
  push("README.md", renderReadme(design, { kind }));
  push("src/server.ts", renderServer(design, { kind }));
  push("src/proxy.ts", renderProxy({ kind }));
  push("src/stdio.ts", renderStdio());
  push("src/http.ts", renderHttp(design, { kind }));
  if (designedKind) {
    push("src/audit.ts", renderAudit());
    push("src/auth.ts", renderAuth(design));
    push("src/middleware.ts", renderMiddleware());
  }

  // Sort for deterministic ordering across runs.
  files.sort((a, b) => a.path.localeCompare(b.path));

  // Canonical digest of the artifact = sha256 of (path,digest) pairs in sorted order.
  const digest = canonicalSha256(files.map((f) => [f.path, f.digest]));

  const generatedAt = opt.generatedAt ?? new Date(0).toISOString();

  return ServerArtifactSchema.parse({
    kind,
    serverName: design.serverName,
    serverVersion: design.serverVersion,
    files,
    digest,
    generatedAt
  });
}
